import { type ReactNode, useEffect, useMemo, useState } from "react";
import "./AppThemeShell.css";

type ThemePreference = "light" | "dark" | "system";

const THEME_STORAGE_KEY = "kunnskapshuset-theme";

const getStoredThemePreference = (): ThemePreference => {
    if (typeof window === "undefined") {
        return "system";
    }

    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
        return stored;
    }

    return "system";
};

const parseBooleanLike = (value: string | undefined): boolean | null => {
    if (!value) {
        return null;
    }

    const normalized = value.toLowerCase();
    if (normalized === "1" || normalized === "true" || normalized === "on" || normalized === "yes") {
        return true;
    }

    if (normalized === "0" || normalized === "false" || normalized === "off" || normalized === "no") {
        return false;
    }

    return null;
};

const getThemeSwitcherFromQuery = (): boolean | null => {
    if (typeof window === "undefined") {
        return null;
    }

    const queryValue = new URLSearchParams(window.location.search).get("themeSwitcher") ?? undefined;
    return parseBooleanLike(queryValue);
};

type AppThemeShellProps = {
    children: ReactNode;
    color?: string;
    showThemeSwitcher?: boolean;
};

export default function AppThemeShell({
    children,
    color = "fjellgronn",
    showThemeSwitcher = false,
}: AppThemeShellProps) {
    const queryOverride = getThemeSwitcherFromQuery();
    const envOverride = parseBooleanLike(import.meta.env.VITE_SHOW_THEME_SWITCHER);
    const shouldShowThemeSwitcher = queryOverride ?? envOverride ?? showThemeSwitcher;

    const [themePreference, setThemePreference] = useState<ThemePreference>(getStoredThemePreference);
    const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
        if (typeof window === "undefined") {
            return true;
        }

        return window.matchMedia("(prefers-color-scheme: dark)").matches;
    });

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
    }, [themePreference]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = (event: MediaQueryListEvent) => {
            setSystemPrefersDark(event.matches);
        };

        setSystemPrefersDark(mediaQuery.matches);
        mediaQuery.addEventListener("change", onChange);

        return () => {
            mediaQuery.removeEventListener("change", onChange);
        };
    }, []);

    const resolvedTheme = useMemo(() => {
        if (themePreference === "system") {
            return systemPrefersDark ? "dark" : "light";
        }

        return themePreference;
    }, [systemPrefersDark, themePreference]);

    return (
        <main data-color={color} data-color-scheme={resolvedTheme} className="app-shell">
            {shouldShowThemeSwitcher && (
                <fieldset className="theme-switch">
                    <legend className="theme-switch__legend">Velg tema</legend>
                    <button
                        type="button"
                        className={`theme-switch__button ${themePreference === "light" ? "is-active" : ""}`}
                        onClick={() => setThemePreference("light")}
                    >
                        Light
                    </button>
                    <button
                        type="button"
                        className={`theme-switch__button ${themePreference === "dark" ? "is-active" : ""}`}
                        onClick={() => setThemePreference("dark")}
                    >
                        Dark
                    </button>
                    <button
                        type="button"
                        className={`theme-switch__button ${themePreference === "system" ? "is-active" : ""}`}
                        onClick={() => setThemePreference("system")}
                    >
                        System
                    </button>
                </fieldset>
            )}
            {children}
        </main>
    );
}
